import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import UserContext from 'shared/components/context/user'
import LoginModal from 'shared/components/login-modal'
import { googleAnalyticsEvent, setGTADataLayer, trackPageView } from 'shared/util/analytics'
import styled from 'styled-components'
import { OverlayBlock } from './components/blocks'
import LegacyFileMap from './components/legacy-file'
import { useCreationAccess } from './hooks/use-creation-access'
import { getBasePathFromCreationTool, getCreationToolFromPath } from './routes'
import { CreationToolFrame, CreationToolSourceFile } from './types'
import CreationTool from './types/creation-tool'
import CreationToolOutputType from './types/output-type'
import CreationToolStep from './types/steps'
import CreationToolUserRestriction from './types/user-restriction'
import getCreationSteps from './utils/get-creation-steps'
import getUploadCanvas from './utils/get-upload-canvas'

const DragDim = styled(OverlayBlock)`
    background: rgba(0, 0, 0, 0.7);
    pointer-events: none;
    position: fixed;
`

export type CreationToolsContextProps = {
    tool: CreationTool
    sources: CreationToolSourceFile[]
    outputType: CreationToolOutputType
    steps: CreationToolStep[]
    stepFrames: CreationToolFrame[]
    currentStep: CreationToolStep | undefined
    currentAudioId: string
    userRestrictionAccess: CreationToolUserRestriction[]
    isDraggingFile: boolean
    setFiles: (sources: CreationToolSourceFile[], outputType: CreationToolOutputType) => void
    setSources: (sources: CreationToolSourceFile[]) => void
    setOutputType: (outputType: CreationToolOutputType) => void
    setCurrentAudioId: (id: string) => void
    saveFrames: (frames: CreationToolFrame[], step: CreationToolStep) => void
    trackEvent: (category: string, action: string, label: string, value?: string) => void
    setIsDraggingFile: (isDraggingFile: boolean) => void
}

export const CreationToolsContext = createContext({} as CreationToolsContextProps)

type Props = {
    children: ReactNode
}

const CreationToolsContextManager = ({ children }: Props) => {
    const { user } = useContext(UserContext)
    const [sources, setSources] = useState<CreationToolSourceFile[]>([])
    const [outputType, setOutputType] = useState<CreationToolOutputType>(CreationToolOutputType.Gif)
    const [steps, setSteps] = useState<CreationToolStep[]>([])
    const [currentAudioId, setCurrentAudioId] = useState<string>('')
    const [showLoginModal, setShowLoginModal] = useState<boolean>(false)
    const [isDraggingFile, setIsDraggingFile] = useState<boolean>(false)
    const stepFramesRef = useRef({})
    const userRestrictionAccess = useCreationAccess()
    const { pathname } = useLocation()
    const { push } = useHistory()
    const tool = getCreationToolFromPath(pathname)
    const basePath = getBasePathFromCreationTool(tool)
    const currentStep = steps.find(step => {
        const route = CreationToolStep.route(step)
        return pathname.indexOf(route) > -1
    })

    useEffect(() => {
        // set user id dimension for ga
        setGTADataLayer({
            dimension: 'dimension8',
            value: user.id,
        })
    }, [user])

    useEffect(() => {
        // TODO: Make sure sticker maker fires as stickermaker and not gifmaker?
        trackPageView(pathname)
    }, [pathname])

    const setFiles = async (sources: CreationToolSourceFile[], outputType: CreationToolOutputType) => {
        // if the tool requires login, show the login modal
        if (CreationTool.requiresLogin(tool) && !user.loggedIn) {
            setShowLoginModal(true)
            return
        }

        const steps = getCreationSteps(sources, tool, outputType)
        setSources(sources)
        setOutputType(outputType)
        setSteps(steps)
        stepFramesRef.current = {}

        if (
            steps.length &&
            (tool === CreationTool.Upload ||
                outputType === CreationToolOutputType.Sticker ||
                tool === CreationTool.SuperCut)
        ) {
            const url = `${basePath}${CreationToolStep.route(steps[0])}`
            push(url)
        }
    }

    const saveFrames = (frames: CreationToolFrame[], step: CreationToolStep) => {
        if (!stepFramesRef.current) return

        if (step === CreationToolStep.Upload) {
            // if storing frames for upload step, hit getUploadCanvas to scale/clean canvas for upload
            stepFramesRef.current[step] = frames.map(({ canvas, frameCount }) => {
                return { canvas: getUploadCanvas(canvas), frameCount }
            })
        } else {
            stepFramesRef.current[step] = frames
        }
    }

    const trackEvent = (category: string, action: string, label: string, value: string) => {
        // TODO: Add stricter typing to these function params if possible?
        googleAnalyticsEvent({ category, action, label, value })
    }

    return (
        <CreationToolsContext.Provider
            value={{
                tool,
                sources,
                outputType,
                currentAudioId,
                steps,
                stepFrames: currentStep ? stepFramesRef.current[currentStep] || [] : [],
                currentStep,
                userRestrictionAccess,
                isDraggingFile,
                setFiles,
                setSources,
                setOutputType,
                setCurrentAudioId,
                setIsDraggingFile,
                saveFrames,
                trackEvent,
            }}
        >
            {children}
            {tool === CreationTool.GifMaker && <LegacyFileMap />}
            {isDraggingFile && <DragDim />}
            {showLoginModal && <LoginModal />}
        </CreationToolsContext.Provider>
    )
}

export default CreationToolsContextManager
